import React, { useState, useEffect } from 'react';
import { useNavigation } from 'context/NavigationContext';
import {
	BringzzCountryDropdown,
	BringzzPageContainer,
	BringzzImage,
	BringzzText,
	BringzzButton
} from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { useAuth } from 'context/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import AnimatedPlaceholderInput from 'components/AnimatedPlaceholderInput';
import './auth.css';

const AuthPage = () => {
	const { navigate } = useNavigation();
	const { loginWithToken } = useAuth();
	const { sendRequest } = useRequest();
	const { t } = useTranslation();

	const [country, setCountry] = useState({
		name: 'Austria',
		dial_code: '+43',
		code: 'AT'
	});

	const [isValidInput, setIsValidInput] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [isSSOAuthProcessing, setIsSSOAuthProcessing] = useState(false);

	const phoneRegex = /^\d{5,}$/;
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	// Load Facebook SDK
	useEffect(() => {
		window.fbAsyncInit = function () {
			window.FB.init({
				appId: process.env.REACT_APP_SSO_FACEBOOK_ID,
				cookie: true,
				xfbml: true,
				version: 'v15.0'
			});
			window.FB.AppEvents.logPageView();
		};

		(function (d, s, id) {
			var js, fjs = d.getElementsByTagName(s)[0];
			if (d.getElementById(id)) { return; }
			js = d.createElement(s); js.id = id;
			js.src = "https://connect.facebook.net/en_US/sdk.js";
			fjs.parentNode.insertBefore(js, fjs);
		}(document, 'script', 'facebook-jssdk'));
	}, []);

	// SSO callback handling
	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const code = urlParams.get('code');
		const state = urlParams.get('state');

		if (code && state) {
			if (state.startsWith('google#')) {
				handleGoogleCallback(code, state);
			} else if (state.startsWith('facebook#')) {
				handleFacebookCallback(code, state);
			}
		}
	}, []);

	const handleGoogleCallback = async (code, state) => {
		setIsSSOAuthProcessing(true);
		try {
			const response = await sendRequest({
				method: 'POST',
				url: `/module/user/googleAuthCallback`,
				data: { code, state }
			});

			if (response.data) {
				if (response.message === "signupRequired") {
					const userData = response.data;
					const preUserResponse = await sendRequest({
						method: 'POST',
						url: `/module/user/preUserCreation`,
						data: {
							login: userData.email,
							password: "",
							logintype: 2,
							language: "en"
						}
					});

					if (preUserResponse.status === 'success' && preUserResponse.data.preuser) {
						const userId = preUserResponse.data.preuser;
						const email = encodeURIComponent(userData.email);
						let profileUrl = `/register/consumer/profile?method=email&userId=${userId}&value=${email}`;
						if (userData.givenName) profileUrl += `&firstName=${encodeURIComponent(userData.givenName)}`;
						if (userData.familyName) profileUrl += `&lastName=${encodeURIComponent(userData.familyName)}`;
						if (userData.picture) profileUrl += `&profilePicture=${encodeURIComponent(userData.picture)}`;
						navigate(profileUrl);
					}
				} else {
					loginWithToken(response.data.token, response.data);
					toast.success(t('component.auth.success.google_login'));
				}
			} else {
				toast.info(t('component.auth.errors.google_login_failed'));
				setIsSSOAuthProcessing(false);
			}
		} catch (error) {
			console.error('Error during Google login callback', error);
			toast.error(t('component.auth.errors.google_login_failed'));
			setIsSSOAuthProcessing(false);
		}
	};

	const handleFacebookCallback = async (code, state) => {
		setIsSSOAuthProcessing(true);
		try {
			const response = await sendRequest({
				method: 'POST',
				url: `/module/user/facebookAuthCallback`,
				data: { code, state }
			});

			if (response.data) {
				if (response.message === "signupRequired") {
					const userData = response.data;
					const preUserResponse = await sendRequest({
						method: 'POST',
						url: `/module/user/preUserCreation`,
						data: {
							login: userData.email,
							password: "",
							logintype: 3,
							language: "en"
						}
					});

					if (preUserResponse.status === 'success' && preUserResponse.data.preuser) {
						const userId = preUserResponse.data.preuser;
						const email = encodeURIComponent(userData.email);
						let profileUrl = `/register/consumer/profile?method=email&userId=${userId}&value=${email}`;
						if (userData.givenName) profileUrl += `&firstName=${encodeURIComponent(userData.givenName)}`;
						if (userData.familyName) profileUrl += `&lastName=${encodeURIComponent(userData.familyName)}`;
						if (userData.picture) profileUrl += `&profilePicture=${encodeURIComponent(userData.picture)}`;
						navigate(profileUrl);
					}
				} else {
					loginWithToken(response.data.token, response.data);
					toast.success(t('component.auth.success.facebook_login'));
				}
			} else {
				toast.info(t('component.auth.errors.facebook_login_failed'));
				setIsSSOAuthProcessing(false);
			}
		} catch (error) {
			console.error('Error during Facebook login callback', error);
			toast.error(t('component.auth.errors.facebook_login_failed'));
			setIsSSOAuthProcessing(false);
		}
	};

	const googleLogin = async () => {
		try {
			const response = await sendRequest({
				method: 'GET',
				url: `/module/user/getLoginUrl`,
			});
			if (response.status === "success" && response.data.loginUrl) {
				window.location.href = response.data.loginUrl;
			} else {
				toast.error(t('component.auth.errors.google_init_failed'));
			}
		} catch (error) {
			toast.error(t('component.auth.errors.google_connection_failed'));
		}
	};

	const handleFacebookLogin = async () => {
		try {
			const response = await sendRequest({
				method: 'GET',
				url: `/module/user/getFacebookLoginUrl`,
			});
			if (response.status === "success" && response.data.loginUrl) {
				window.location.href = response.data.loginUrl;
			} else {
				toast.error(t('component.auth.errors.facebook_init_failed'));
			}
		} catch (error) {
			toast.error(t('component.auth.errors.facebook_connection_failed'));
		}
	};

	const handleAppleSignIn = () => {
		try {
			const clientId = 'de.mysucurema.bringzzdev.service';
			const redirectUri = `${process.env.REACT_APP_USE_BACKEND}/module/user/redirectWithCode`;
			const scope = 'email name';
			const state = 'apple#tokenLogin';
			const responseMode = 'form_post';
			const responseType = 'code';
			const authorizationUrl = `https://appleid.apple.com/auth/authorize?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(state)}&response_mode=${encodeURIComponent(responseMode)}&response_code=${responseType}`;
			window.location.href = authorizationUrl;
		} catch (error) {
			toast.error(t('component.auth.errors.apple_connection_failed'));
		}
	};

	const handleNavigation = () => {
		if (!isValidInput) return;

		const method = phoneRegex.test(inputValue) ? 'phone' : 'email';
		const value = method === 'phone'
			? `${country.dial_code}%20${inputValue}`
			: inputValue;

		navigate(`/login?method=${method}&value=${encodeURIComponent(value)}`);
	};

	if (isSSOAuthProcessing) {
		return (
			<BringzzPageContainer className='bg-magic-lilac auth-no-scroll' authenticated={false}>
				<div className='flex justify-center items-center h-full'>
					<BringzzText className='text-midnight-blue font-medium text-lg'>
						{"Processing ... Please wait."}
					</BringzzText>
				</div>
			</BringzzPageContainer>
		);
	}

	return (
		<BringzzPageContainer
			className='bg-magic-lilac auth-no-scroll'
			authenticated={false}
			footer={
				<div className='pb-20 px-6'>
					<div className='flex flex-col px-4'>
						<BringzzText className='text-mignight-blue font-normal text-xs'>
							{t('component.auth.agreement.prefix')}{' '}<br />
							{t('component.auth.agreement.agree_to')}{' '}
							<span className='font-medium underline'>
								{t('component.auth.agreement.privacy_policy')}
							</span>{' '}
							{t('component.auth.agreement.and')}{' '}
							<span className='font-medium underline'>
								{t('component.auth.agreement.terms_of_use')}
							</span>
						</BringzzText>
					</div>
					<div className='flex justify-center mt-2'>
						<BringzzButton
							size='lg'
							className='bg-midnight-blue w-full flex justify-center text-white'
							onClick={handleNavigation}
							disabled={!isValidInput}
						>
							{!isValidInput && t('component.auth.buttons.continue')}
							{isValidInput && t('component.auth.buttons.continue')}
						</BringzzButton>
					</div>
				</div>
			}
		>
			<div className='flex flex-col h-full w-full overflow-hidden'>
				<div className='flex flex-col h-1/2 px-4 justify-between'>
					<div className='flex grow justify-center items-center'>
						<BringzzImage className="h-14" src="BringzzLogo.png" alt={t('component.auth.logo_alt')} />
					</div>
					<form
						className='flex mt-6'
						onSubmit={(e) => {
							e.preventDefault();
							if (isValidInput) {
								handleNavigation();
							}
						}}
					>
						<AnimatedPlaceholderInput
							containerClassname="w-full"
							className="!pl-16"
							type='text'
							placeholders={[t('component.auth.input.phone'), t('component.auth.input.email')]}
							value={inputValue}
							onChange={(e, isValid, newValue) => {
								setIsValidInput(isValid);
								setInputValue(newValue);
							}}
							onKeyDown={(e) => {
								if ((e.key === 'Enter' || e.key === 'Return' || e.keyCode === 13) && isValidInput) {
									e.preventDefault();
									handleNavigation();
								}
							}}
							pattern='^(?:\+?\d{1,3}[-\s.]?\d{3,}|\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+)$'
							icon={{
								left: <BringzzCountryDropdown
									className="!rounded-r-none"
									mode={'modal'}
									onCountryChange={result => {
										setCountry(result);
									}}
								/>
							}}
						/>
						<button type="submit" style={{ display: 'none' }} />
					</form>
				</div>
				<div className='h-1/2 px-6 w-full'>
					<div className='flex items-center gap-x-4 mt-6'>
						<div className='grow bg-sand h-[1px]'></div>
						<div>
							<BringzzText tag='h4' className='!text-sand font-normal'>
								{t('component.auth.divider')}
							</BringzzText>
						</div>
						<div className='grow bg-sand h-[1px]'></div>
					</div>
					<div className='flex justify-center space-x-6 pt-12'>
						<div className="flex justify-center items-center w-14 h-14 border border-midnight-blue cursor-pointer rounded-full overflow-hidden"
							onClick={handleAppleSignIn}
						>
							<BringzzImage className='h-8' src="/icons/apple.png" alt={t('component.auth.social.apple')} />
						</div>
						<div className="flex justify-center cursor-pointer items-center w-14 h-14 border border-midnight-blue rounded-full overflow-hidden"
							onClick={handleFacebookLogin}
						>
							<BringzzImage className='h-8' src="/icons/facebook.png" alt={t('component.auth.social.facebook')} />
						</div>
						<div className="flex justify-center cursor-pointer items-center w-14 h-14 border border-midnight-blue rounded-full overflow-hidden"
							onClick={googleLogin}
						>
							<BringzzImage className='h-8' src="/icons/google.png" alt={t('component.auth.social.google')} />
						</div>
					</div>
				</div>
			</div>
		</BringzzPageContainer>
	);
};

export default AuthPage;
